/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 */

/**
 * Quick Schedule — Add to assignment modal (accessibility alternative to drag-and-drop).
 *
 * Mirrors the step-2 assignment panel inside a dialog. Changes stay in a local draft until Done;
 * step 2 updates only from onDone. Two interaction modes on each card:
 * - First click: assignTargetAssignmentId (preview merge on that card; no selection border — same as step 2 after drop).
 * - Second click on the same populated card: editingAssignmentId (click-to-edit; Clear data).
 *
 * @file QuickScheduleAddToAssignmentModal.jsx
 * @author Anuja Gaikwad
 * @since 2.4.3
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import Icon from '../icons/Icon';
import AssignmentList from '../assignments/AssignmentList';
import {
  QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG,
  applyAddNewAssignmentSlot,
  applyAddNodeToAssignmentSlot,
  applyAssignmentTitleUpdate,
  applyClearAssignmentContent,
  applyRemoveAssignmentSlot,
  applySectionRemove
} from './quickScheduleReviewUtils';
import {
  QuickScheduleAddToAssignmentCloseButton,
  QuickScheduleAddToAssignmentDialogContent,
  QuickScheduleAddToAssignmentDialogPaper,
  QuickScheduleAddToAssignmentDoneButton,
  QuickScheduleAddToAssignmentFooter,
  QuickScheduleAddToAssignmentListWrapper,
  QuickScheduleAddToNewAssignmentButton,
  QuickScheduleAddToAssignmentScrollPane,
  QuickScheduleAddToAssignmentSubtitle,
  QuickScheduleAddToAssignmentTitle
} from './styles/quickScheduleAddToAssignmentModalStyles';

function resolveNodeTitle(node) {
  if (!node) {
    return '';
  }

  return node.title || node.label || '';
}

function formatModalSubtitle(template, nodeTitle) {
  if (!template) {
    return nodeTitle;
  }

  return template.replace(/\{title\}/g, nodeTitle);
}

function stripAssignmentDisplayFlags(assignment) {
  if (!assignment) {
    return assignment;
  }

  const { hasNoQuestionsWarning, ...rest } = assignment;

  return rest;
}

/**
 * Creates an independent copy of the parent assignments.
 *
 * The copy is used as the local draft while the modal is open.
 * Changes to the draft do not modify the parent assignments.
 */
function cloneAssignmentsForDraft(assignments = []) {
  return assignments.map((assignment) => stripAssignmentDisplayFlags(
    JSON.parse(JSON.stringify(assignment))
  ));
}

const QuickScheduleAddToAssignmentModal = ({
  open,
  onClose,
  onDone,
  assignments,
  selectedNode,
  sourceTocData,
  scheduleFormValues,
  textInfo,
  autoNameAssignments,
  onAssignmentRemove,
  onAssignmentSlotRemove,
  onSectionRemove
}) => {
  /**
   * Assignment that will receive the selected TOC node.
   */
  const [assignTargetAssignmentId, setAssignTargetAssignmentId] = useState('');

  /**
   * Assignment currently being edited.
   */
  const [editingAssignmentId, setEditingAssignmentId] = useState('');

  /**
   * Local working copy of assignments.
   *
   * All changes made inside the modal are stored here.
   * The parent assignments are updated only when Done is clicked.
   */
  const [draftAssignments, setDraftAssignments] = useState([]);

  /**
   * Draft as it was before the selected TOC node was placed on a card, so
   * "Add to new assignment" puts the node on the new card instead of the picked one.
   */
  const [draftBeforeAssign, setDraftBeforeAssign] = useState(null);

  const modalTitle = textInfo.addToAssignmentModalTitle || 'Add to assignment';
  const doneLabel = textInfo.addToAssignmentModalDoneLabel || 'Done';
  const addToNewAssignmentLabel = textInfo.addToNewAssignmentLabel || 'Add to new assignment';
  const nodeTitle = resolveNodeTitle(selectedNode);
  const subtitle = formatModalSubtitle(
    textInfo.addToAssignmentModalSubtitle,
    nodeTitle
  );

  /**
   * Create a fresh draft whenever the modal opens.
   */
  useEffect(() => {
    if (!open) {
      setAssignTargetAssignmentId('');
      setEditingAssignmentId('');
      setDraftBeforeAssign(null);

      return;
    }

    setDraftAssignments(cloneAssignmentsForDraft(assignments));
    setAssignTargetAssignmentId('');
    setEditingAssignmentId('');
    setDraftBeforeAssign(null);
  }, [open, assignments]);

  const listItemConfig = useMemo(() => ({
    ...QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG,
    showTitle: !autoNameAssignments,
    showDragZone: true,
    showDragZoneIcon: false,
    selectionMode: false,
    inlineEditOnSelect: false,
    clickToEdit: false,
    modalScheduleAssignMode: true,
    assignTargetAssignmentId,
    showSectionRemoveButtons: true,
    showClearDataButton: true,
    suppressScheduleDateEdit: true
  }), [assignTargetAssignmentId, autoNameAssignments]);

  /**
   * Selects an assignment and immediately adds the selected TOC node
   * to the local draft.
   *
   * Nothing is sent to the parent until Done is clicked.
   */
  const handleAssignTargetSelect = useCallback((assignment) => {
    setAssignTargetAssignmentId((previousId) => (
      previousId === assignment.id ? '' : assignment.id
    ));

    setEditingAssignmentId('');

    // Add the selected TOC node directly to the local draft.
    if (selectedNode?.id) {
      setDraftBeforeAssign((previousDraft) => previousDraft || draftAssignments);
      setDraftAssignments((previousAssignments) => {
        const result = applyAddNodeToAssignmentSlot({
          assignments: previousAssignments,
          assignmentId: assignment.id,
          nodePayload: selectedNode,
          sourceTocData
        });

        return result.assignments.map(stripAssignmentDisplayFlags);
      });
    }
  }, [draftAssignments, selectedNode, sourceTocData]);

  /**
   * Enters or exits edit mode for an assignment.
   */
  const handleAssignmentEdit = useCallback((assignment) => {
    setEditingAssignmentId((previousId) => (
      previousId === assignment.id ? '' : assignment.id
    ));
  }, []);

  /**
   * Exits assignment edit mode.
   */
  const handleAssignmentDeselect = useCallback(() => {
    setEditingAssignmentId('');
  }, []);

  /**
   * Updates the assignment title in the local draft.
   */
  const handleAssignmentTitleChange = useCallback((assignment, title) => {
    const result = applyAssignmentTitleUpdate({
      assignments: draftAssignments,
      assignmentId: assignment.id,
      title
    });

    setDraftAssignments(
      result.assignments.map(stripAssignmentDisplayFlags)
    );
  }, [draftAssignments]);

  /**
   * Removes a section from an assignment.
   */
  const handleSectionRemove = useCallback((assignment, sectionNumber) => {
    const result = applySectionRemove({
      assignments: draftAssignments,
      assignmentId: assignment.id,
      sectionNumber
    });

    const updatedAssignments = result.assignments.map(
      stripAssignmentDisplayFlags
    );

    setDraftAssignments(updatedAssignments);

    onSectionRemove?.({
      assignmentId: assignment.id,
      sectionNumber,
      assignments: updatedAssignments
    });

    const updatedAssignment = updatedAssignments.find(
      (item) => item.id === assignment.id
    );

    const slotIsEmpty = !updatedAssignment?.sections?.length
      && !updatedAssignment?.contentNodes?.length;

    if (slotIsEmpty) {
      setAssignTargetAssignmentId((previousId) => (
        previousId === assignment.id ? '' : previousId
      ));

      setEditingAssignmentId((previousId) => (
        previousId === assignment.id ? '' : previousId
      ));
    }
  }, [draftAssignments, onSectionRemove]);

  /**
   * Clears an assignment if it contains content.
   * Removes the assignment slot if it is already empty.
   */
  const handleAssignmentSlotRemove = useCallback((assignment) => {
    const hasContent = (
      assignment.contentNodes?.length
      || assignment.sections?.length
      || 0
    ) > 0;

    if (hasContent) {
      const result = applyClearAssignmentContent({
        assignments: draftAssignments,
        assignmentId: assignment.id
      });

      const nextAssignments = result.assignments.map(stripAssignmentDisplayFlags);

      setDraftAssignments(nextAssignments);
      onAssignmentRemove?.({
        assignmentId: assignment.id,
        assignments: nextAssignments
      });
    } else {
      const result = applyRemoveAssignmentSlot({
        assignments: draftAssignments,
        assignmentId: assignment.id
      });

      const nextAssignments = result.assignments.map(stripAssignmentDisplayFlags);

      setDraftAssignments(nextAssignments);
      onAssignmentSlotRemove?.({
        assignmentId: assignment.id,
        assignments: nextAssignments
      });
    }

    setAssignTargetAssignmentId((previousId) => (
      previousId === assignment.id ? '' : previousId
    ));

    setEditingAssignmentId((previousId) => (
      previousId === assignment.id ? '' : previousId
    ));
  }, [draftAssignments, onAssignmentRemove, onAssignmentSlotRemove]);

  /**
   * Sends the local draft to the parent.
   *
   * This is the only point where the modal commits its changes
   * to the parent component.
   */
  const handleDoneClick = useCallback(() => {
    const targetAssignment = assignTargetAssignmentId
      ? draftAssignments.find(
        (assignment) => assignment.id === assignTargetAssignmentId
      )
      : null;

    onDone?.({
      assignments: draftAssignments,
      targetAssignmentId: assignTargetAssignmentId || '',
      targetDateIso: targetAssignment?.date || ''
    });
  }, [
    assignTargetAssignmentId,
    draftAssignments,
    onDone
  ]);

  /**
   * Adds an undated card holding the selected TOC node to the top of the schedule
   * and commits through onDone; the host closes the modal and step 2 scrolls to the card.
   */
  const handleAddToNewAssignmentClick = useCallback(() => {
    const result = applyAddNewAssignmentSlot({
      assignments: draftBeforeAssign || draftAssignments,
      scheduleFormValues,
      nodePayload: selectedNode,
      sourceTocData
    });

    onDone?.({
      assignments: result.assignments.map(stripAssignmentDisplayFlags),
      targetAssignmentId: result.targetAssignmentId || '',
      targetDateIso: '',
      isNewAssignment: true
    });
  }, [
    draftAssignments,
    draftBeforeAssign,
    onDone,
    scheduleFormValues,
    selectedNode,
    sourceTocData
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperComponent={QuickScheduleAddToAssignmentDialogPaper}
    >
      <QuickScheduleAddToAssignmentCloseButton
        onClick={onClose}
        aria-label="Close"
      >
        <Icon name="CloseIcon" />
      </QuickScheduleAddToAssignmentCloseButton>

      <QuickScheduleAddToAssignmentDialogContent>
        <QuickScheduleAddToAssignmentTitle component="h2">
          {modalTitle}
        </QuickScheduleAddToAssignmentTitle>

        {Boolean(subtitle) && (
          <QuickScheduleAddToAssignmentSubtitle component="p">
            {subtitle}
          </QuickScheduleAddToAssignmentSubtitle>
        )}

        <QuickScheduleAddToAssignmentScrollPane>
          <QuickScheduleAddToAssignmentListWrapper>
            <AssignmentList
              assignments={draftAssignments}
              textInfo={textInfo}
              selectedAssignmentId={editingAssignmentId}
              listItemConfig={listItemConfig}
              onSelect={handleAssignTargetSelect}
              onEdit={handleAssignmentEdit}
              onDeselect={handleAssignmentDeselect}
              onRemove={handleAssignmentSlotRemove}
              onSectionRemove={handleSectionRemove}
              onTitleChange={handleAssignmentTitleChange}
            />
          </QuickScheduleAddToAssignmentListWrapper>
        </QuickScheduleAddToAssignmentScrollPane>

        <QuickScheduleAddToAssignmentFooter>
          <QuickScheduleAddToNewAssignmentButton
            type="button"
            onClick={handleAddToNewAssignmentClick}
            disabled={!selectedNode?.id}
          >
            {addToNewAssignmentLabel}
          </QuickScheduleAddToNewAssignmentButton>
          <QuickScheduleAddToAssignmentDoneButton
            type="button"
            onClick={handleDoneClick}
          >
            {doneLabel}
          </QuickScheduleAddToAssignmentDoneButton>
        </QuickScheduleAddToAssignmentFooter>
      </QuickScheduleAddToAssignmentDialogContent>
    </Dialog>
  );
};

QuickScheduleAddToAssignmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  assignments: PropTypes.arrayOf(PropTypes.object),
  selectedNode: PropTypes.object,
  sourceTocData: PropTypes.arrayOf(PropTypes.object),
  scheduleFormValues: PropTypes.object,
  textInfo: PropTypes.object,
  autoNameAssignments: PropTypes.bool,
  onAssignmentRemove: PropTypes.func,
  onAssignmentSlotRemove: PropTypes.func,
  onSectionRemove: PropTypes.func
};

QuickScheduleAddToAssignmentModal.defaultProps = {
  open: false,
  onClose: () => {},
  onDone: () => {},
  assignments: [],
  selectedNode: null,
  sourceTocData: [],
  scheduleFormValues: {},
  textInfo: {},
  autoNameAssignments: true,
  onAssignmentRemove: null,
  onAssignmentSlotRemove: null,
  onSectionRemove: null
};

export default QuickScheduleAddToAssignmentModal;
